import React, { useState, useEffect, useCallback } from 'react';
import { Bell, X, AlertTriangle, CheckCircle, Info, XCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';

interface Notice {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'success' | 'error';
  created_at: string;
  user_id: string | null;
  is_read: boolean;
  is_dismissed: boolean;
}

interface NoticeBoardProps {
  showCompact?: boolean;
  onToggle?: () => void;
}

const NoticeBoard: React.FC<NoticeBoardProps> = ({ showCompact = false, onToggle }) => {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(true);
  const [userCreatedAt, setUserCreatedAt] = useState<string | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation();

  const fetchNotices = useCallback(async () => {
    try {
      setLoading(true);

      if (!user?.id) {
        console.log('No user ID available');
        setNotices([]);
        return;
      }

      // First, get the user's creation date to filter notices
      let userCreationDate = userCreatedAt;
      if (!userCreationDate) {
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('created_at')
          .eq('id', user.id)
          .single();

        if (!userError && userData) {
          userCreationDate = userData.created_at;
          setUserCreatedAt(userData.created_at);
        }
      }

      // Fetch user's dismissals from notice_dismissals table
      const { data: dismissals, error: dismissalError } = await supabase
        .from('notice_dismissals')
        .select('notice_id, is_read, is_dismissed')
        .eq('user_id', user.id);

      if (dismissalError) {
        console.error('Error fetching dismissals:', dismissalError);
        // Continue anyway - just won't have dismissal info
      }

      // Create a map of dismissals for quick lookup
      const dismissalMap = new Map<string, { is_read: boolean; is_dismissed: boolean }>();
      (dismissals || []).forEach(d => {
        dismissalMap.set(d.notice_id, { is_read: d.is_read, is_dismissed: d.is_dismissed });
      });

      // Fetch all notices - RLS will filter based on user's context
      const { data, error } = await supabase
        .from('notices')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching notices:', error);
        toast({
          title: t('notices.loadErrorTitle'),
          description: t('notices.loadErrorDesc'),
          variant: "destructive",
        });
        setNotices([]);
        return;
      }

      // Filter and transform notices
      const filteredNotices = (data || [])
        .filter(notice => {
          // Check if user has dismissed this notice
          const userDismissal = dismissalMap.get(notice.id);
          if (userDismissal?.is_dismissed) {
            return false;
          }

          // If notice is targeted to a specific user, always show it (regardless of date)
          if (notice.user_id === user.id) {
            return true;
          }

          // For org/building/global notices, only show if created after user joined
          if (userCreationDate && notice.created_at < userCreationDate) {
            return false;
          }

          return true;
        })
        .map(notice => {
          const userDismissal = dismissalMap.get(notice.id);
          return {
            ...notice,
            type: notice.type as 'info' | 'warning' | 'success' | 'error',
            is_read: userDismissal?.is_read ?? false,
            is_dismissed: userDismissal?.is_dismissed ?? false
          };
        });

      setNotices(filteredNotices);

    } catch (error: unknown) {
      console.error('Error fetching notices:', error);
      toast({
        title: t('notices.loadErrorTitle'),
        description: t('notices.loadErrorDesc'),
        variant: "destructive",
      });
      setNotices([]);
    } finally {
      setLoading(false);
    }
  }, [user, userCreatedAt, toast, t]);

  useEffect(() => {
    if (user) {
      fetchNotices();
    }
  }, [user, fetchNotices]);

  const unreadCount = notices.filter(notice => !notice.is_read).length;

  const markAsRead = async (id: string) => {
    if (!user?.id) return;

    try {
      // Upsert into notice_dismissals table (per-user tracking)
      const { error } = await supabase
        .from('notice_dismissals')
        .upsert({
          notice_id: id,
          user_id: user.id,
          is_read: true,
          is_dismissed: false
        }, {
          onConflict: 'notice_id,user_id'
        });

      if (error) throw error;

      setNotices(notices.map(notice =>
        notice.id === id ? { ...notice, is_read: true } : notice
      ));
    } catch (error) {
      console.error('Error marking notice as read:', error);
      // Local fallback
      setNotices(notices.map(notice =>
        notice.id === id ? { ...notice, is_read: true } : notice
      ));
    }
  };

  const dismissNotice = async (id: string) => {
    if (!user?.id) return;

    try {
      // Upsert into notice_dismissals table (per-user tracking)
      const { error } = await supabase
        .from('notice_dismissals')
        .upsert({
          notice_id: id,
          user_id: user.id,
          is_read: true,
          is_dismissed: true
        }, {
          onConflict: 'notice_id,user_id'
        });

      if (error) throw error;

      setNotices(notices.filter(notice => notice.id !== id));
    } catch (error) {
      console.error('Error dismissing notice:', error);
      // Local fallback
      setNotices(notices.filter(notice => notice.id !== id));
    }
  };

  const getNoticeIcon = (type: string) => {
    switch (type) {
      case 'warning': return <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5" />;
      case 'success': return <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />;
      case 'error': return <XCircle className="w-4 h-4 sm:w-5 sm:h-5" />;
      default: return <Info className="w-4 h-4 sm:w-5 sm:h-5" />;
    }
  };

  const getNoticeColor = (type: string) => {
    switch (type) {
      case 'warning': return 'text-yellow-600 bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800';
      case 'success': return 'text-green-600 bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800';
      case 'error': return 'text-red-600 bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800';
      default: return 'text-blue-600 bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffMinutes < 1) return t('notices.justNow');
    if (diffMinutes < 60) return t('notices.minutesAgo', { count: diffMinutes });
    if (diffHours < 24) return t('notices.hoursAgo', { count: diffHours });
    if (diffDays === 1) return t('notices.oneDayAgo');
    return t('notices.daysAgo', { count: diffDays });
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4"></div>
          <div className="space-y-3">
            <div className="h-20 bg-gray-200 rounded"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (notices.length === 0) {
    return (
      <div className="bg-card rounded-xl shadow-sm border border-border p-8">
        <div className="flex flex-col items-center justify-center text-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {t('notices.allCaughtUp')}
            </h3>
            <p className="text-sm text-muted-foreground max-w-md">
              {t('notices.noNewNotices')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const displayNotices = showCompact ? notices.slice(0, 2) : notices;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
      <div 
        onClick={onToggle}
        className={`flex items-center justify-between p-4 ${onToggle ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors' : ''}`}
      >
        <div className="flex items-center space-x-3">
          <Bell className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500" />
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
            {t('notices.pageTitle')}
          </h3>
          {unreadCount > 0 && (
            <span className="px-2 py-1 bg-red-500 text-white text-xs rounded-full min-w-[1.25rem] text-center">
              {unreadCount}
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {t('notices.count', { count: notices.length })}
          </div>
          {onToggle && (
            showCompact ? (
              <ChevronDown className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronUp className="w-5 h-5 text-gray-500" />
            )
          )}
        </div>
      </div>

      {!showCompact && (
        <div className="px-4 pb-4 space-y-3">
          {notices.map((notice) => (
          <div
            key={notice.id}
            className={`p-3 rounded-lg border transition-all ${getNoticeColor(notice.type)} ${
              !notice.is_read ? 'shadow-sm' : 'opacity-75'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3 flex-1 min-w-0">
                <div className="flex-shrink-0 mt-0.5">
                  {getNoticeIcon(notice.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2">
                    <h4 className="font-medium text-gray-900 dark:text-white text-sm sm:text-base">
                      {notice.title}
                    </h4>
                    <span className="text-xs text-gray-500 dark:text-gray-400 mt-1 sm:mt-0">
                      {formatTimestamp(notice.created_at)}
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                    {notice.message}
                  </p>
                  <div className="flex items-center space-x-3 mt-3">
                    {!notice.is_read && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          markAsRead(notice.id);
                        }}
                        className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                      >
                        {t('notifications.markRead')}
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        dismissNotice(notice.id);
                      }}
                      className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      {t('notices.dismiss')}
                    </button>
                  </div>
                </div>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  dismissNotice(notice.id);
                }}
                className="flex-shrink-0 ml-2 p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
              >
                <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              </button>
            </div>
          </div>
          ))}
        </div>
      )}

      {showCompact && displayNotices.length > 0 && (
        <div className="px-4 pb-4 space-y-2">
          {displayNotices.map((notice) => (
            <div
              key={notice.id}
              className={`p-2 rounded-lg border transition-all ${getNoticeColor(notice.type)} ${
                !notice.is_read ? 'shadow-sm' : 'opacity-75'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-2 flex-1 min-w-0">
                  <div className="flex-shrink-0 mt-0.5">
                    {getNoticeIcon(notice.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-900 dark:text-white text-sm truncate">
                      {notice.title}
                    </h4>
                    <p className="text-xs text-gray-700 dark:text-gray-300 line-clamp-1">
                      {notice.message}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-1 ml-2">
                  {!notice.is_read && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        markAsRead(notice.id);
                      }}
                      className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                    >
                      ✓
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      dismissNotice(notice.id);
                    }}
                    className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                  >
                    <X className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>
              </div>
            </div>
          ))}
          {notices.length > 2 && (
            <div className="text-center pt-2">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {t('notices.moreNotices', { count: notices.length - 2 })}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NoticeBoard;